import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, Calendar, Clock, User, GripVertical, 
  CheckCircle2, Circle, PlayCircle, AlertCircle, Trash2, 
  Paperclip, X, Upload, ChevronLeft, ChevronRight, ListTodo,
  Play, Square, Timer, Target
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_CONFIG = {
  todo: { label: 'De făcut', icon: Circle, color: 'bg-slate-100 text-slate-700', borderColor: 'border-slate-300' },
  in_progress: { label: 'În lucru', icon: PlayCircle, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-300' },
  done: { label: 'Finalizat', icon: CheckCircle2, color: 'bg-green-100 text-green-700', borderColor: 'border-green-300' },
  blocked: { label: 'Blocat', icon: AlertCircle, color: 'bg-red-100 text-red-700', borderColor: 'border-red-300' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Scăzută', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medie', color: 'bg-yellow-100 text-yellow-700' },
  high: { label: 'Ridicată', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgentă', color: 'bg-red-100 text-red-700' }
};

// Format minutes to human readable
const formatDuration = (minutes) => {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Parse date string YYYY-MM-DD to local Date (fixes timezone issues)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Format date for display
const formatDateDisplay = (dateStr, options = {}) => {
  const date = parseLocalDate(dateStr);
  if (!date) return '';
  return date.toLocaleDateString('ro-RO', options);
};

// Generate 15-minute time slots
const TIME_SLOTS = [];
for (let h = 8; h <= 20; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hour = h.toString().padStart(2, '0');
    const min = m.toString().padStart(2, '0');
    TIME_SLOTS.push(`${hour}:${min}`);
  }
}

// ======= WEEKLY PLANNING VIEW =======
const DAY_NAMES = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri'];

const getWeekDays = (offset = 0) => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday + offset * 7);
  
  return DAY_NAMES.map((name, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      name,
      date: d.toISOString().split('T')[0],
      dayNum: d.getDate(),
      monthShort: d.toLocaleDateString('ro-RO', { month: 'short' }),
      isToday: d.toISOString().split('T')[0] === new Date().toISOString().split('T')[0]
    };
  });
};

const WeeklyPlanningView = ({ tasks, onEditTask, setTasks, user, activeTimer, handleStartTimer, handleStopTimer, timerTick, getRunningTime, formatDuration, users }) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [userCapacity, setUserCapacity] = useState(null);
  const [approvedSummary, setApprovedSummary] = useState(null);
  
  const weekDays = getWeekDays(weekOffset);
  const weekStart = weekDays[0].date;
  const weekEnd = weekDays[4].date;
  
  // Fetch user capacity + approved suggestions
  useEffect(() => {
    const loadCapacityAndSuggestions = async () => {
      try {
        // Get user profile for daily capacity
        const profileRes = await api.get('/auth/me');
        setUserCapacity(profileRes.data?.daily_capacity_hours || 6);
      } catch { setUserCapacity(6); }
      
      try {
        // Get approved peer review summary
        const summaryRes = await api.get(`/peer-review/my-reviews`);
        if (summaryRes.data?.approved_summary) {
          setApprovedSummary(summaryRes.data.approved_summary);
        }
      } catch { /* no summary */ }
    };
    loadCapacityAndSuggestions();
  }, []);
  
  // Tasks for this week grouped by day
  const weekTasks = {};
  weekDays.forEach(d => { weekTasks[d.date] = []; });
  
  tasks.forEach(t => {
    if (t.scheduled_date >= weekStart && t.scheduled_date <= weekEnd) {
      if (weekTasks[t.scheduled_date]) {
        weekTasks[t.scheduled_date].push(t);
      }
    }
  });
  
  // Sort tasks by scheduled_time within each day
  Object.keys(weekTasks).forEach(date => {
    weekTasks[date].sort((a, b) => (a.scheduled_time || '99:99').localeCompare(b.scheduled_time || '99:99'));
  });
  
  const wHandleDragStart = (e, task) => {
    setDraggedTaskId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
    requestAnimationFrame(() => {
      e.target.style.opacity = '0.4';
    });
  };
  
  const wHandleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTaskId(null);
    setDragOverDay(null);
  };
  
  const wHandleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverDay !== date) setDragOverDay(date);
  };
  
  const wHandleDragLeave = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const { clientX: x, clientY: y } = e;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverDay(null);
    }
  };
  
  const wHandleDrop = async (e, newDate) => {
    e.preventDefault();
    setDragOverDay(null);
    const taskId = draggedTaskId;
    if (!taskId) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.scheduled_date === newDate) { setDraggedTaskId(null); return; }
    
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, scheduled_date: newDate } : t));
    setDraggedTaskId(null);
    
    try {
      await api.put(`/tasks/${taskId}`, { ...task, scheduled_date: newDate });
    } catch {
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, scheduled_date: task.scheduled_date } : t));
      toast.error('Eroare la mutarea taskului');
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Planificare Săptămânală
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)} data-testid="week-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant={weekOffset === 0 ? "default" : "outline"} size="sm" onClick={() => setWeekOffset(0)}>
              Săpt. curentă
            </Button>
            <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)} data-testid="week-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {weekDays[0].dayNum} {weekDays[0].monthShort} – {weekDays[4].dayNum} {weekDays[4].monthShort}
        </p>
      </CardHeader>
      <CardContent className="p-0 pb-4 px-4">
        <div className="grid grid-cols-5 gap-2" data-testid="weekly-planning-grid">
          {weekDays.map((day) => {
            const isOver = dragOverDay === day.date;
            const dayTasks = weekTasks[day.date];
            const totalMin = dayTasks.reduce((s, t) => s + Math.round((t.estimated_hours || 0) * 60), 0);
            
            return (
              <div
                key={day.date}
                className={`rounded-lg border-2 transition-all duration-150 min-h-[400px] flex flex-col ${
                  day.isToday ? 'border-blue-400 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'
                } ${isOver ? 'border-blue-500 bg-blue-100/50 scale-[1.01] shadow-lg' : ''}`}
                onDragOver={(e) => wHandleDragOver(e, day.date)}
                onDragLeave={wHandleDragLeave}
                onDrop={(e) => wHandleDrop(e, day.date)}
                data-testid={`week-day-${day.date}`}
              >
                {/* Day Header */}
                <div className={`px-3 py-2 border-b ${day.isToday ? 'bg-blue-100/60' : 'bg-slate-100/60'} rounded-t-lg`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-bold text-sm ${day.isToday ? 'text-blue-700' : ''}`}>{day.name}</span>
                      <span className="text-xs text-muted-foreground ml-1">{day.dayNum}</span>
                    </div>
                    {day.isToday && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-500 text-white border-0">Azi</Badge>}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-muted-foreground">{dayTasks.length} task{dayTasks.length !== 1 ? 'uri' : ''}</span>
                    <span className="text-[11px] font-medium">{totalMin}min</span>
                  </div>
                </div>
                
                {/* Tasks - same as Kanban cards */}
                <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto">
                  {dayTasks.map((task) => {
                    const isMyActiveTimer = activeTimer?.task_id === task.id;
                    const hasOtherTimer = task.has_active_timer && !isMyActiveTimer;
                    
                    return (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => wHandleDragStart(e, task)}
                        onDragEnd={wHandleDragEnd}
                        className={`bg-white rounded-md border p-2.5 cursor-grab active:cursor-grabbing hover:shadow-md transition-all duration-150 ${
                          draggedTaskId === task.id ? 'opacity-40 scale-95' : 'opacity-100'
                        } ${isMyActiveTimer ? 'ring-2 ring-green-400' : ''}`}
                        data-testid={`weekly-task-${task.id}`}
                      >
                        {/* Title + project */}
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-xs leading-tight line-clamp-2">{task.title}</p>
                            <p className="text-[10px] text-muted-foreground truncate">{task.project_name}</p>
                          </div>
                          <GripVertical className="w-3 h-3 text-slate-300 flex-shrink-0 mt-0.5" />
                        </div>
                        
                        {/* Scheduled time + recurring */}
                        {task.scheduled_time && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-blue-600">
                            <Calendar className="w-2.5 h-2.5" />
                            {task.scheduled_time}
                            {task.is_recurring && <Badge variant="outline" className="ml-1 text-[8px] px-1 py-0">Rec</Badge>}
                          </div>
                        )}
                        
                        {/* Time tracking */}
                        <div className="mt-1 flex items-center gap-1 text-[10px]">
                          <Clock className="w-2.5 h-2.5 text-slate-400" />
                          <span className={task.tracked_minutes > 0 || isMyActiveTimer ? 'text-green-600 font-medium' : 'text-slate-400'}>
                            {formatDuration((task.tracked_minutes || 0) + (isMyActiveTimer ? getRunningTime(activeTimer.start_time) : 0) + (isMyActiveTimer ? timerTick * 0 : 0))}
                          </span>
                          {task.estimated_hours > 0 && (
                            <span className="text-slate-400">/ {Math.round(task.estimated_hours * 60)}min</span>
                          )}
                        </div>
                        
                        {/* Priority + Status + assigned user */}
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          <Badge className={`text-[9px] px-1 py-0 ${PRIORITY_CONFIG[task.priority]?.color || ''}`}>
                            {PRIORITY_CONFIG[task.priority]?.label}
                          </Badge>
                          {task.assigned_to_name && task.assigned_to_name !== 'Unassigned' && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <User className="w-2.5 h-2.5" />
                              {task.assigned_to_name?.split(' ')[0]}
                            </span>
                          )}
                        </div>
                        
                        {/* Status selector - clickable */}
                        <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                          <Select 
                            value={task.status} 
                            onValueChange={async (newStatus) => {
                              setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
                              try {
                                await api.patch(`/tasks/${task.id}/status`, { status: newStatus });
                              } catch {
                                setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: task.status } : t));
                                toast.error('Eroare la schimbarea statusului');
                              }
                            }}
                          >
                            <SelectTrigger className={`h-5 text-[10px] px-1.5 py-0 border rounded w-full gap-1 ${STATUS_CONFIG[task.status]?.color}`} data-testid={`weekly-status-${task.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                <SelectItem key={key} value={key}>
                                  <span className={`${cfg.color} px-1.5 py-0.5 rounded text-xs`}>{cfg.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Timer & Edit buttons */}
                        <div className="mt-1.5 flex justify-between items-center">
                          {isMyActiveTimer ? (
                            <Button size="sm" variant="destructive" className="h-6 text-[10px] px-2"
                              onClick={(e) => { e.stopPropagation(); handleStopTimer(task.id); }}
                              data-testid={`weekly-stop-${task.id}`}
                            >
                              <Square className="w-2.5 h-2.5 mr-0.5" /> Stop
                            </Button>
                          ) : hasOtherTimer ? (
                            <span className="text-[10px] text-orange-500 flex items-center gap-0.5">
                              <Timer className="w-2.5 h-2.5" /> Timer activ
                            </span>
                          ) : !activeTimer ? (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-green-600 border-green-300 hover:bg-green-50"
                              onClick={(e) => { e.stopPropagation(); handleStartTimer(task); }}
                              data-testid={`weekly-start-${task.id}`}
                            >
                              <Play className="w-2.5 h-2.5 mr-0.5" /> Start
                            </Button>
                          ) : (
                            <span className="text-[9px] text-slate-400">Alt timer</span>
                          )}
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                            onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
                            data-testid={`weekly-edit-${task.id}`}
                          >
                            Editează
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {dayTasks.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="text-xs">Niciun task</p>
                    </div>
                  )}
                </div>
                
                {/* Day Status Footer - capacity status */}
                {userCapacity && (() => {
                  const capMinutes = userCapacity * 60;
                  const availMinutes = capMinutes - totalMin;
                  const availHours = (availMinutes / 60).toFixed(1);
                  
                  return (
                    <div className={`px-3 py-2 border-t rounded-b-lg text-center ${
                      availMinutes > capMinutes * 0.3 ? 'bg-green-50' : 
                      availMinutes > 0 ? 'bg-orange-50' : 'bg-red-50'
                    }`}>
                      {availMinutes > 0 ? (
                        <span className={`text-[11px] font-bold ${availMinutes > capMinutes * 0.3 ? 'text-green-700' : 'text-orange-600'}`}>
                          Underloaded {availHours}h
                        </span>
                      ) : availMinutes === 0 ? (
                        <span className="text-[11px] font-bold text-blue-600">
                          Complet
                        </span>
                      ) : (
                        <span className="text-[11px] font-bold text-red-600">
                          Overloaded {Math.abs(availHours)}h
                        </span>
                      )}
                      <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1">
                        <div 
                          className={`h-1.5 rounded-full transition-all ${
                            availMinutes > capMinutes * 0.3 ? 'bg-green-500' : 
                            availMinutes > 0 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((totalMin / capMinutes) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-muted-foreground">{totalMin}min / {capMinutes}min cap.</span>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
        
        {/* Recommendations from approved peer review */}
        {approvedSummary && (approvedSummary.summary_text || approvedSummary.suggestions?.length > 0) && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
            <h4 className="text-sm font-bold flex items-center gap-2 mb-2 text-amber-800">
              <Target className="w-4 h-4 text-amber-500" />
              Recomandări de Îmbunătățire
            </h4>
            {approvedSummary.summary_text && (
              <p className="text-sm text-slate-700 mb-3">{approvedSummary.summary_text}</p>
            )}
            {approvedSummary.suggestions?.length > 0 && (
              <div className="space-y-1.5">
                {approvedSummary.suggestions.map((s, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-amber-600 font-bold mt-0.5">{i + 1}.</span>
                    <p className="text-slate-700">{s}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban');
  // Default filter: everyone sees their own tasks first
  const [filters, setFilters] = useState({ 
    assignedTo: '', 
    dateFrom: '', 
    dateTo: '' 
  });
  const [showFilters, setShowFilters] = useState(false);
  const [myTasksOnly, setMyTasksOnly] = useState(true);
  
  // Task dialog
  const [taskDialog, setTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', project_id: '', priority: 'medium',
    status: 'todo', assigned_to: '', estimated_hours: '', estimated_minutes: '', due_date: '',
    scheduled_date: '', scheduled_time: '', assigned_users: []
  });
  
  // Project search
  const [projectSearch, setProjectSearch] = useState('');
  const [projectSuggestions, setProjectSuggestions] = useState([]);
  
  // Daily planning date
  const [planningDate, setPlanningDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Drag state
  const [draggedTask, setDraggedTask] = useState(null);
  
  // Timer state
  const [activeTimer, setActiveTimer] = useState(null);
  const [timerTick, setTimerTick] = useState(0);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    loadData();
    loadActiveTimer();
    
    // Cleanup timer on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);
  
  // Timer tick effect - update every minute
  useEffect(() => {
    if (activeTimer) {
      // Update immediately
      setTimerTick(t => t + 1);
      // Then update every 60 seconds (1 minute)
      timerIntervalRef.current = setInterval(() => {
        setTimerTick(t => t + 1);
      }, 60000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [activeTimer]);

  useEffect(() => {
    if (projectSearch.length > 1) {
      searchProjects(projectSearch);
    } else {
      setProjectSuggestions([]);
    }
  }, [projectSearch]);

  // Reload when filters change (for admin/project_manager)
  useEffect(() => {
    if (user && user.role !== 'team_member') {
      loadData();
    }
  }, [filters.assignedTo, filters.dateFrom, filters.dateTo, myTasksOnly]);
  
  const loadActiveTimer = async () => {
    try {
      const res = await api.get('/users/me/active-timer');
      if (res.data.has_active_timer) {
        setActiveTimer(res.data);
      } else {
        setActiveTimer(null);
      }
    } catch (err) {
      console.error('Failed to load active timer');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Build query params - team_member sees only their own tasks by default
      let taskParams = new URLSearchParams();
      if (user?.role === 'team_member') {
        taskParams.append('assigned_to', user.id);
      } else if (filters.assignedTo) {
        taskParams.append('assigned_to', filters.assignedTo);
      } else if (myTasksOnly) {
        taskParams.append('assigned_to', user.id);
      }
      if (filters.dateFrom) taskParams.append('date_from', filters.dateFrom);
      if (filters.dateTo) taskParams.append('date_to', filters.dateTo);
      
      const taskUrl = `/tasks/all${taskParams.toString() ? '?' + taskParams.toString() : ''}`;
      
      const [tasksRes, projectsRes, usersRes] = await Promise.all([
        api.get(taskUrl),
        api.get('/projects'),
        api.get('/users')
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const searchProjects = async (query) => {
    try {
      const res = await api.get(`/projects/search?q=${encodeURIComponent(query)}`);
      setProjectSuggestions(res.data);
    } catch (err) {
      console.error('Search failed');
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setTaskForm({
      title: '', description: '', project_id: '', priority: 'medium',
      status: 'todo', assigned_to: user?.id || '', estimated_hours: '', estimated_minutes: '', due_date: '',
      scheduled_date: planningDate, scheduled_time: '',
      is_recurring: false, recurring_frequency: 1, recurring_period: 'week', recurring_end_date: '',
      assigned_users: []
    });
    setProjectSearch('');
    setTaskDialog(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    // estimated_hours stores total time as decimal hours (e.g. 0.5 = 30min)
    // We need to extract hours and minutes from it
    // DO NOT add estimated_minutes separately - it's already included in estimated_hours
    const totalMinutesFromHours = Math.round((task.estimated_hours || 0) * 60);
    const hours = Math.floor(totalMinutesFromHours / 60);
    const minutes = totalMinutesFromHours % 60;
    
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      project_id: task.project_id || '',
      priority: task.priority || 'medium',
      status: task.status || 'todo',
      assigned_to: task.assigned_to || '',
      assigned_users: task.assigned_users || [],
      estimated_hours: hours || '',
      estimated_minutes: minutes || '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      scheduled_date: task.scheduled_date || '',
      scheduled_time: task.scheduled_time || '',
      is_recurring: task.is_recurring || false,
      recurring_frequency: task.recurring_frequency || 1,
      recurring_period: task.recurring_period || 'week',
      recurring_end_date: task.recurring_end_date || ''
    });
    setProjectSearch(task.project_name || '');
    setTaskDialog(true);
  };
  
  // Timer functions
  const handleStartTimer = async (task) => {
    try {
      const res = await api.post(`/tasks/${task.id}/start-timer`);
      toast.success(`Timer pornit pentru "${task.title}"`);
      setActiveTimer({
        has_active_timer: true,
        task_id: task.id,
        task_title: task.title,
        start_time: res.data.start_time
      });
      // Update task in list - also set status to in_progress if it was todo
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { 
          ...t, 
          has_active_timer: true, 
          active_timer_user_id: user.id, 
          active_timer_start: res.data.start_time,
          status: t.status === 'todo' ? 'in_progress' : t.status 
        } : t
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la pornirea timer-ului');
    }
  };
  
  const handleStopTimer = async (taskId) => {
    try {
      const res = await api.post(`/tasks/${taskId}/stop-timer`);
      toast.success(`Timer oprit - ${res.data.duration_formatted}`);
      setActiveTimer(null);
      // Reload data to get accurate tracked_minutes from server
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la oprirea timer-ului');
    }
  };
  
  // Calculate running time for active timer
  const getRunningTime = (startTime) => {
    if (!startTime) return 0;
    const start = new Date(startTime);
    const now = new Date();
    return Math.floor((now - start) / 1000 / 60); // minutes
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Titlul este obligatoriu');
      return;
    }
    if (!taskForm.project_id) {
      toast.error('Selectează un proiect');
      return;
    }

    try {
      // Calculate total estimated time in decimal hours
      // estimated_hours field in form = hours part, estimated_minutes = minutes part
      const hours = parseInt(taskForm.estimated_hours) || 0;
      const minutes = parseInt(taskForm.estimated_minutes) || 0;
      const totalMinutes = hours * 60 + minutes;
      
      // Rule: max 60 minutes per task
      if (totalMinutes > 60) {
        toast.error('Un task nu poate depăși 60 de minute (1h). Împarte în taskuri mai mici.');
        return;
      }
      
      const totalHours = totalMinutes / 60;
      
      const payload = {
        ...taskForm,
        estimated_hours: totalHours > 0 ? totalHours : null,
        estimated_minutes: minutes,
        due_date: taskForm.due_date || null,
        assigned_users: taskForm.assigned_users || []
      };

      if (editingTask) {
        const res = await api.put(`/tasks/${editingTask.id}`, payload);
        toast.success('Task actualizat!');
        // Optimistic update
        setTasks(prev => prev.map(t => t.id === editingTask.id ? { ...t, ...payload, ...res.data } : t));
      } else {
        const res = await api.post('/tasks', payload);
        toast.success('Task creat!');
        // Optimistic add
        setTasks(prev => [...prev, res.data]);
      }
      setTaskDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Sigur vrei să ștergi acest task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task șters');
      loadData();
    } catch (err) {
      toast.error('Eroare la ștergere');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      toast.error('Eroare la actualizare');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    // Add dragging visual
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTask(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }
    
    // Optimistic update - UI updates instantly
    const taskId = draggedTask.id;
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    setDraggedTask(null);
    
    // Then sync with backend
    try {
      await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
    } catch (err) {
      // Revert on error
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: draggedTask.status } : t));
      toast.error('Eroare la actualizare status');
    }
  };

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filters.assignedTo && task.assigned_to !== filters.assignedTo) return false;
    return true;
  });

  // Group tasks by status for Kanban
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    in_progress: filteredTasks.filter(t => t.status === 'in_progress'),
    done: filteredTasks.filter(t => t.status === 'done'),
    blocked: filteredTasks.filter(t => t.status === 'blocked')
  };

  // Daily planning tasks
  const dailyTasks = filteredTasks.filter(t => t.scheduled_date === planningDate);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Active Timer Banner */}
        {activeTimer && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              <span className="font-medium text-green-800">Timer activ:</span>
              <span className="text-green-700">{activeTimer.task_title}</span>
              <Badge variant="outline" className="bg-green-100 text-green-800">
                <Timer className="w-3 h-3 mr-1" />
                {/* timerTick forces re-render every minute */}
                {formatDuration(getRunningTime(activeTimer.start_time))}
              </Badge>
            </div>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => handleStopTimer(activeTimer.task_id)}
              data-testid="stop-active-timer-btn"
            >
              <Square className="w-4 h-4 mr-1" />
              Stop
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ListTodo className="w-7 h-7 text-blue-500" />
              Task Management
            </h1>
            <p className="text-muted-foreground">Gestionează și planifică task-urile echipei</p>
          </div>
          <div className="flex gap-2">
            {user?.role !== 'team_member' && (
              <Button 
                variant={myTasksOnly ? "default" : "outline"} 
                onClick={() => {
                  setMyTasksOnly(!myTasksOnly);
                  if (!myTasksOnly) {
                    setFilters(f => ({...f, assignedTo: ''}));
                  }
                }}
                data-testid="my-tasks-toggle-btn"
              >
                <User className="w-4 h-4 mr-2" />
                {myTasksOnly ? 'Taskurile Mele' : 'Toate Taskurile'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Filtre
            </Button>
            <Button onClick={handleCreateTask} data-testid="create-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Task Nou
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Asignat lui</Label>
                  <Select value={filters.assignedTo || 'all'} onValueChange={(v) => setFilters({...filters, assignedTo: v === 'all' ? '' : v})}>
                    <SelectTrigger data-testid="filter-assigned-to">
                      <SelectValue placeholder="Toți utilizatorii" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toți</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>De la data</Label>
                  <Input 
                    type="date" 
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                    data-testid="filter-date-from"
                  />
                </div>
                <div>
                  <Label>Până la data</Label>
                  <Input 
                    type="date" 
                    value={filters.dateTo}
                    onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Tabs */}
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban Board</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">Listă</TabsTrigger>
            <TabsTrigger value="daily" data-testid="tab-daily">Planificare Săptămânală</TabsTrigger>
          </TabsList>

          {/* Kanban View */}
          <TabsContent value="kanban">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const StatusIcon = config.icon;
                const statusTasks = tasksByStatus[status] || [];
                
                return (
                  <div 
                    key={status}
                    className={`rounded-lg border-2 ${config.borderColor} bg-slate-50 min-h-[400px] transition-colors duration-200`}
                    onDragOver={(e) => {
                      handleDragOver(e);
                      e.currentTarget.classList.add('bg-blue-50', 'border-blue-300');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300');
                    }}
                    onDrop={(e) => {
                      e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300');
                      handleDrop(e, status);
                    }}
                    data-testid={`kanban-column-${status}`}
                  >
                    <div className={`p-3 ${config.color} rounded-t-md flex items-center justify-between`}>
                      <div className="flex items-center gap-2">
                        <StatusIcon className="w-4 h-4" />
                        <span className="font-medium">{config.label}</span>
                      </div>
                      <Badge variant="secondary">{statusTasks.length}</Badge>
                    </div>
                    
                    <div className="p-2 space-y-2">
                      {statusTasks.map(task => {
                        const isMyActiveTimer = activeTimer?.task_id === task.id;
                        const hasOtherTimer = task.has_active_timer && !isMyActiveTimer;
                        
                        return (
                          <div
                            key={task.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, task)}
                            onDragEnd={handleDragEnd}
                            className={`bg-white rounded-md border p-3 cursor-grab hover:shadow-md transition-all duration-200 ${isMyActiveTimer ? 'ring-2 ring-green-400' : ''}`}
                            data-testid={`task-card-${task.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{task.title}</p>
                                <p className="text-xs text-muted-foreground truncate">{task.project_name}</p>
                              </div>
                              <GripVertical className="w-4 h-4 text-slate-300 flex-shrink-0" />
                            </div>
                            
                            {/* Scheduled date/time */}
                            {(task.scheduled_date || task.scheduled_time) && (
                              <div className="mt-1 flex items-center gap-1 text-xs text-blue-600">
                                <Calendar className="w-3 h-3" />
                                {task.scheduled_date && formatDateDisplay(task.scheduled_date, { day: 'numeric', month: 'short' })}
                                {task.scheduled_time && <span className="ml-1">{task.scheduled_time}</span>}
                                {task.is_recurring && <Badge variant="outline" className="ml-1 text-[10px] px-1">Recurent</Badge>}
                              </div>
                            )}
                            
                            {/* Time tracking info */}
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className={task.tracked_minutes > 0 || isMyActiveTimer ? 'text-green-600 font-medium' : 'text-slate-400'}>
                                {/* timerTick ensures re-render every minute when timer is active */}
                                {formatDuration((task.tracked_minutes || 0) + (isMyActiveTimer ? getRunningTime(activeTimer.start_time) : 0) + (isMyActiveTimer ? timerTick * 0 : 0))}
                              </span>
                              {task.estimated_hours && (
                                <span className="text-slate-400">/ {Math.round(task.estimated_hours * 60)}min est.</span>
                              )}
                            </div>
                            
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <Badge className={`text-xs ${PRIORITY_CONFIG[task.priority]?.color || ''}`}>
                                {PRIORITY_CONFIG[task.priority]?.label}
                              </Badge>
                              {task.assigned_to_name && task.assigned_to_name !== 'Unassigned' && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_to_name?.split(' ')[0]}
                                </span>
                              )}
                            </div>
                            
                            {/* Timer & Edit buttons */}
                            <div className="mt-2 flex justify-between items-center">
                              {/* Timer button */}
                              {isMyActiveTimer ? (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  className="h-7 text-xs"
                                  onClick={() => handleStopTimer(task.id)}
                                  data-testid={`stop-timer-${task.id}`}
                                >
                                  <Square className="w-3 h-3 mr-1" />
                                  Stop
                                </Button>
                              ) : hasOtherTimer ? (
                                <span className="text-xs text-orange-500 flex items-center gap-1">
                                  <Timer className="w-3 h-3" />
                                  Timer activ
                                </span>
                              ) : !activeTimer ? (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs text-green-600 border-green-300 hover:bg-green-50"
                                  onClick={() => handleStartTimer(task)}
                                  data-testid={`start-timer-${task.id}`}
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  Start
                                </Button>
                              ) : (
                                <span className="text-xs text-slate-400">Alt timer activ</span>
                              )}
                              
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleEditTask(task)}
                                data-testid={`edit-task-${task.id}`}
                              >
                                Editează
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* List View */}
          <TabsContent value="list">
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-medium">Task</th>
                      <th className="text-left p-3 font-medium">Proiect</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Programat</th>
                      <th className="text-left p-3 font-medium">Timp</th>
                      <th className="text-left p-3 font-medium">Asignat</th>
                      <th className="text-right p-3 font-medium">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map(task => {
                      const isMyActiveTimer = activeTimer?.task_id === task.id;
                      
                      return (
                        <tr key={task.id} className={`border-b hover:bg-slate-50 ${isMyActiveTimer ? 'bg-green-50' : ''}`} data-testid={`task-row-${task.id}`}>
                          <td className="p-3">
                            <div>
                              <span className="font-medium">{task.title}</span>
                              {task.is_recurring && (
                                <Badge variant="outline" className="ml-2 text-[10px]">Recurent</Badge>
                              )}
                            </div>
                            <Badge className={`text-xs mt-1 ${PRIORITY_CONFIG[task.priority]?.color}`}>
                              {PRIORITY_CONFIG[task.priority]?.label}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{task.project_name}</td>
                          <td className="p-3">
                            <Badge className={STATUS_CONFIG[task.status]?.color}>
                              {STATUS_CONFIG[task.status]?.label}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">
                            {task.scheduled_date ? (
                              <div className="flex items-center gap-1 text-blue-600">
                                <Calendar className="w-3 h-3" />
                                {formatDateDisplay(task.scheduled_date, { day: 'numeric', month: 'short' })}
                                {task.scheduled_time && <span className="font-medium">{task.scheduled_time}</span>}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="p-3 text-sm">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              <span className={task.tracked_minutes > 0 || isMyActiveTimer ? 'text-green-600 font-medium' : 'text-slate-400'}>
                                {/* timerTick ensures re-render every minute */}
                                {formatDuration((task.tracked_minutes || 0) + (isMyActiveTimer ? getRunningTime(activeTimer.start_time) : 0) + (isMyActiveTimer ? timerTick * 0 : 0))}
                              </span>
                              {task.estimated_hours && (
                                <span className="text-slate-400">/ {Math.round(task.estimated_hours * 60)}min</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-sm">{task.assigned_to_name !== 'Unassigned' ? task.assigned_to_name : '-'}</td>
                          <td className="p-3 text-right space-x-1">
                            {/* Timer button */}
                            {isMyActiveTimer ? (
                              <Button size="sm" variant="destructive" className="h-7" onClick={() => handleStopTimer(task.id)}>
                                <Square className="w-3 h-3 mr-1" />
                                Stop
                              </Button>
                            ) : !activeTimer && (
                              <Button size="sm" variant="outline" className="h-7 text-green-600" onClick={() => handleStartTimer(task)}>
                                <Play className="w-3 h-3 mr-1" />
                                Start
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleEditTask(task)}>
                              Editează
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                    {filteredTasks.length === 0 && (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-muted-foreground">
                          Nu există task-uri. Creează primul task!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Planning View */}
          <TabsContent value="daily">
            <WeeklyPlanningView 
              tasks={filteredTasks}
              onEditTask={handleEditTask}
              setTasks={setTasks}
              user={user}
              activeTimer={activeTimer}
              handleStartTimer={handleStartTimer}
              handleStopTimer={handleStopTimer}
              timerTick={timerTick}
              getRunningTime={getRunningTime}
              formatDuration={formatDuration}
              users={users}
            />
          </TabsContent>
        </Tabs>

        {/* Task Dialog */}
        <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Editează Task' : 'Task Nou'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Titlu *</Label>
                <Input 
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                  placeholder="Descriere scurtă a task-ului"
                  data-testid="task-title-input"
                />
              </div>
              
              <div>
                <Label>Proiect *</Label>
                <div className="relative">
                  <Input 
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      setTaskForm({...taskForm, project_id: ''});
                    }}
                    placeholder="Caută proiect..."
                    data-testid="task-project-search"
                  />
                  {projectSuggestions.length > 0 && !taskForm.project_id && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                      {projectSuggestions.map(p => (
                        <div 
                          key={p.id}
                          className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                          onClick={() => {
                            setTaskForm({...taskForm, project_id: p.id});
                            setProjectSearch(p.name);
                            setProjectSuggestions([]);
                          }}
                        >
                          {p.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {taskForm.project_id && (
                  <p className="text-xs text-green-600 mt-1">✓ Proiect selectat</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={taskForm.status} onValueChange={(v) => setTaskForm({...taskForm, status: v})}>
                    <SelectTrigger data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioritate</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Asignat lui</Label>
                  <Select value={taskForm.assigned_to || "__none__"} onValueChange={(v) => setTaskForm({...taskForm, assigned_to: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="task-assigned-select">
                      <SelectValue placeholder="Selectează" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Neasignat</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Timp estimat (max 60 min)</Label>
                  <div className="flex gap-2 items-center">
                    <Input 
                      type="number"
                      min="0"
                      max="60"
                      value={(() => {
                        const h = parseInt(taskForm.estimated_hours) || 0;
                        const m = parseInt(taskForm.estimated_minutes) || 0;
                        return h * 60 + m || '';
                      })()}
                      onChange={(e) => {
                        const totalMin = Math.min(parseInt(e.target.value) || 0, 60);
                        setTaskForm({...taskForm, estimated_hours: '0', estimated_minutes: totalMin.toString()});
                      }}
                      placeholder="ex: 30"
                      className="w-24"
                      data-testid="task-minutes-input"
                    />
                    <span className="text-sm text-muted-foreground">minute</span>
                  </div>
                </div>
              </div>
              
              {/* Multiple assignees for meetings */}
              <div>
                <Label>Participanți adițional (pentru meeting-uri)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {users.filter(u => u.id !== taskForm.assigned_to).map(u => {
                    const isSelected = (taskForm.assigned_users || []).includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          const current = taskForm.assigned_users || [];
                          if (isSelected) {
                            setTaskForm({...taskForm, assigned_users: current.filter(id => id !== u.id)});
                          } else {
                            setTaskForm({...taskForm, assigned_users: [...current, u.id]});
                          }
                        }}
                        className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                          isSelected 
                            ? 'bg-blue-100 border-blue-300 text-blue-700' 
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        {u.full_name}
                      </button>
                    );
                  })}
                </div>
                {(taskForm.assigned_users || []).length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {(taskForm.assigned_users || []).length} participanți selectați
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Deadline</Label>
                  <Input 
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                    data-testid="task-due-date"
                  />
                </div>
                <div>
                  <Label>Data planificată</Label>
                  <Input 
                    type="date"
                    value={taskForm.scheduled_date}
                    onChange={(e) => setTaskForm({...taskForm, scheduled_date: e.target.value})}
                    data-testid="task-scheduled-date"
                  />
                </div>
              </div>
              
              {taskForm.scheduled_date && (
                <div>
                  <Label>Ora programată (intervale 15 min)</Label>
                  <Select value={taskForm.scheduled_time || "__none__"} onValueChange={(v) => setTaskForm({...taskForm, scheduled_time: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="task-scheduled-time">
                      <SelectValue placeholder="Selectează ora" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Fără oră specifică</SelectItem>
                      {TIME_SLOTS.map(slot => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Recurring Task Section */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="is_recurring"
                    checked={taskForm.is_recurring}
                    onChange={(e) => setTaskForm({...taskForm, is_recurring: e.target.checked})}
                    className="w-4 h-4 rounded border-slate-300"
                    data-testid="task-recurring-checkbox"
                  />
                  <Label htmlFor="is_recurring" className="cursor-pointer font-medium">
                    Task recurent
                  </Label>
                </div>
                
                {taskForm.is_recurring && (
                  <div className="space-y-3 pl-6">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Frecvență</Label>
                        <Select 
                          value={String(taskForm.recurring_frequency)} 
                          onValueChange={(v) => setTaskForm({...taskForm, recurring_frequency: parseInt(v)})}
                        >
                          <SelectTrigger data-testid="task-recurring-frequency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9,10].map(n => (
                              <SelectItem key={n} value={String(n)}>La fiecare {n}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Perioadă</Label>
                        <Select 
                          value={taskForm.recurring_period} 
                          onValueChange={(v) => setTaskForm({...taskForm, recurring_period: v})}
                        >
                          <SelectTrigger data-testid="task-recurring-period">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="day">Zi/Zile</SelectItem>
                            <SelectItem value="week">Săptămână/Săptămâni</SelectItem>
                            <SelectItem value="month">Lună/Luni</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Până la data (opțional)</Label>
                      <Input 
                        type="date"
                        value={taskForm.recurring_end_date}
                        onChange={(e) => setTaskForm({...taskForm, recurring_end_date: e.target.value})}
                        data-testid="task-recurring-end-date"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Lasă gol pentru a genera automat pe 3 luni
                      </p>
                    </div>
                    <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                      Va crea automat task-uri pentru fiecare {taskForm.recurring_frequency} {taskForm.recurring_period === 'day' ? 'zi(le)' : taskForm.recurring_period === 'week' ? 'săptămână(i)' : 'lună(i)'}
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Descriere</Label>
                <Textarea 
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                  placeholder="Detalii suplimentare despre task..."
                  rows={3}
                  data-testid="task-description"
                />
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setTaskDialog(false)}>Anulează</Button>
              <Button onClick={handleSaveTask} data-testid="save-task-btn">
                {editingTask ? 'Salvează' : 'Creează Task'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Tasks;
